import { isManager } from "@/lib/access";
import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useActiveSession } from "@/context/ActiveSessionContext";
import { RelabelDialog } from "@/components/RelabelDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Timer, User, Users, ArrowLeft, Plus, Pencil, Trash2, FileSpreadsheet } from "lucide-react";

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

const fmtHM = (secs) => {
  if ((secs || 0) > 0 && secs < 60) return `${secs}s`;
  const h = Math.floor((secs || 0) / 3600);
  const m = Math.floor(((secs || 0) % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

const REVIEW_MIN_SECONDS = 15 * 60;
const ASLEEP_STRIPES = { backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 3px, transparent 3px 7px)" };
const needsReview = (s) => !s.running && !s.relabelled_from && (s.asleep || (s.kind === "idle" && s.seconds >= REVIEW_MIN_SECONDS));

const AsleepTag = ({ id }) => (
  <span data-testid={`asleep-tag-${id}`} className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">Device asleep</span>
);

const SEG_COLORS = {
  task: "bg-emerald-500",
  break: "bg-amber-500",
  meeting: "bg-sky-500",
  call: "bg-violet-500",
  idle: "bg-slate-300",
};
const SEG_EDGE = { task: "border-l-emerald-500", break: "border-l-amber-500", meeting: "border-l-sky-500", call: "border-l-violet-500", idle: "border-l-slate-300" };
const SEG_TEXT = { task: "text-emerald-700", break: "text-amber-700", meeting: "text-sky-700", call: "text-violet-700", idle: "text-slate-500" };
const isBlip = (s) => s.kind === "idle" && !s.running && (s.seconds || 0) < 60;
const KIND_ORDER = ["task", "meeting", "call", "break", "idle"];
const TypeChips = ({ totals, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {KIND_ORDER.map((k) => (
      <span key={k} data-testid={`${testid}-${k}`} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${(totals[k] || 0) > 0 ? `${SEG_TEXT[k]} bg-white border-slate-200` : "text-slate-400 bg-slate-50 border-slate-100"}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${SEG_COLORS[k]}`} />
        <span className="uppercase tracking-wider text-[10px] font-semibold">{k}</span>
        <span className="mono tabular-nums text-slate-800">{fmtHM(totals[k] || 0)}</span>
      </span>
    ))}
  </div>
);
const dayTotals = (segs) => segs.reduce((a, s) => ({ ...a, [s.kind]: (a[s.kind] || 0) + (s.seconds || 0) }), {});
// Join a task split by a device-sleep gap (before · asleep · after) back into one row for display.
const mergeSleepSplits = (segs) => segs.reduce((rows, s) => {
  const last = rows[rows.length - 1];
  const tail = last?.parts[last.parts.length - 1];
  const joins = tail && tail.stopped_at && tail.kind === s.kind && tail.label === s.label && (s.asleep || tail.asleep)
    && Math.abs(new Date(s.started_at) - new Date(tail.stopped_at)) <= 5000;
  if (!joins) return [...rows, { ...s, parts: [s] }];
  last.parts.push(s);
  Object.assign(last, { stopped_at: s.stopped_at, running: s.running, seconds: last.seconds + (s.seconds || 0), outside_office: last.outside_office || s.outside_office });
  return rows;
}, []);
const KindTag = ({ kind }) => (
  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold ${SEG_TEXT[kind] || SEG_TEXT.task}`}>
    <span className={`w-2 h-2 rounded-full ${SEG_COLORS[kind] || SEG_COLORS.task}`} />{kind}
  </span>
);

function isoDaysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

// Convert an ISO datetime to a local `datetime-local` value (YYYY-MM-DDTHH:MM)
function toLocalDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDT(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

export default function Timesheet() {
  const { user } = useAuth();
  const isAdmin = isManager(user);
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [preset, setPreset] = useState("30d");
  const [scope, setScope] = useState("mine");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [drillUser, setDrillUser] = useState(null); // {user_id, user_name}
  const [drillDate, setDrillDate] = useState(todayIso());

  const tzOffset = -new Date().getTimezoneOffset();

  const [todayOffset, setTodayOffset] = useState(0); // 0 = today, 1 = yesterday
  const [reviewSeg, setReviewSeg] = useState(null);
  const loadToday = useCallback(async () => {
    const d = new Date(); d.setDate(d.getDate() - todayOffset);
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try { const { data } = await api.get(`/activity/daily?tz_offset_min=${tzOffset}&day=${day}`); setToday(data); }
    catch (e) { console.warn("Today's activity refresh failed:", e); }
  }, [tzOffset, todayOffset]);
  useEffect(() => { loadToday(); const iv = setInterval(loadToday, 30000); return () => clearInterval(iv); }, [loadToday]);
  // Refresh immediately when a timer/idle segment starts or stops anywhere in the app.
  const { session: liveSession } = useActiveSession();
  const liveSessionId = liveSession?.session_id;
  useEffect(() => { loadToday(); }, [liveSessionId, loadToday]);

  const setPresetVal = (p) => {
    setPreset(p);
    const t = todayIso();
    if (p === "today") { setFrom(t); setTo(t); }
    else if (p === "7d") { setFrom(isoDaysAgo(7)); setTo(t); }
    else if (p === "30d") { setFrom(isoDaysAgo(30)); setTo(t); }
    else if (p === "month") {
      const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
      setFrom(start); setTo(t);
    }
    else if (p === "year") {
      const d = new Date(); const start = new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
      setFrom(start); setTo(t);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/timesheet/summary?from=${from}&to=${to}&scope=${scope}&tz_offset_min=${tzOffset}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [from, to, scope, tzOffset]);
  useEffect(() => { load(); }, [load]);

  const download = async (ext) => {
    try {
      const resp = await api.get(`/timesheet/export.${ext}?from=${from}&to=${to}&scope=${scope}&tz_offset_min=${tzOffset}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheet-${scope}-${from}-to-${to}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (drillUser) {
    return (
      <UserDetailedView
        userId={drillUser.user_id}
        userName={drillUser.user_name}
        initialDate={drillDate}
        isAdmin={isAdmin}
        onBack={() => setDrillUser(null)}
      />
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Reports</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Time sheet</h1>
          <p className="text-sm text-slate-500 mt-1">
            {scope === "team" ? "Every hour tracked across the team." : "Everything you've clocked, ready to print or download."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="timesheet-download-xlsx" onClick={() => download("xlsx")} className="bg-blue-700 hover:bg-blue-800 text-white gap-2"><FileSpreadsheet className="w-4 h-4" /> Download Excel</Button>
          <Button data-testid="timesheet-download" variant="outline" onClick={() => download("csv")} className="gap-2 bg-white"><Download className="w-4 h-4" /> CSV</Button>
        </div>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-md p-1 w-fit">
          <button data-testid="ts-scope-mine" onClick={() => setScope("mine")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "mine" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><User className="w-3.5 h-3.5" /> My hours</button>
          <button data-testid="ts-scope-team" onClick={() => setScope("team")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "team" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><Users className="w-3.5 h-3.5" /> Team hours</button>
        </div>
      )}

      {/* Today's activity — daily timeline strip + segment list + Idle rollup */}
      {today && (
        <div data-testid="today-panel" className="bg-white border border-slate-200 rounded-md p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
                {[[0, "Today"], [1, "Yesterday"]].map(([o, l]) => (
                  <button key={o} data-testid={`today-switch-${o}`} onClick={() => setTodayOffset(o)}
                          className={`uppercase tracking-wider ${todayOffset === o ? "text-zinc-950 font-semibold underline underline-offset-4" : "hover:text-blue-700"}`}>{l}</button>
                ))}
                <span>· {today.date}</span>
              </div>
              <h2 className="text-lg font-semibold tracking-tight mt-0.5">{todayOffset === 0 ? "Your day so far" : "Your day yesterday"}</h2>
            </div>
            <div className="text-xs text-slate-500">
              Unassigned / Idle: <span data-testid="today-idle-total" className="mono font-medium text-slate-800">{fmtHM(today.idle_seconds)}</span>
            </div>
          </div>
          <div className="mb-3"><TypeChips totals={dayTotals(today.segments)} testid="today-chips" /></div>
          {/* Timeline strip: 24h horizontal bar with coloured segments */}
          <div className="relative h-8 rounded-sm bg-slate-100 overflow-hidden border border-slate-200" data-testid="today-timeline">
            {today.segments.map((s) => {
              const t0 = new Date(s.started_at).getTime();
              const t1 = s.stopped_at ? new Date(s.stopped_at).getTime() : Date.now();
              // Use LOCAL midnight of the returned day (not UTC midnight) so segments render at
              // the correct horizontal position across timezones.
              const [Y, M, D] = today.date.split("-").map((x) => parseInt(x, 10));
              const dayStart = new Date(Y, M - 1, D, 0, 0, 0).getTime();
              const dayLen = 24 * 3600 * 1000;
              const left = Math.max(0, ((t0 - dayStart) / dayLen) * 100);
              const width = Math.max(0.3, ((t1 - t0) / dayLen) * 100);
              const color = SEG_COLORS[s.kind] || SEG_COLORS.task;
              return (
                <div key={s.id} title={`${s.kind}: ${s.label}${s.asleep ? " (device asleep)" : ""}`} className={`absolute h-full ${color}`} style={{ left: `${left}%`, width: `${width}%`, ...(s.asleep ? ASLEEP_STRIPES : {}) }} />
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Task</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Break</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-sky-500" /> Meeting</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-violet-500" /> Call</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300" /> Idle</span>
          </div>
          {today.segments.length > 0 && (
            <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
              {mergeSleepSplits(today.segments.filter((s) => !isBlip(s))).reverse().map((s) => {
                const sleeps = s.parts.filter((p) => p.asleep);
                const merged = s.parts.length > 1;
                const toReview = s.parts.find(needsReview);
                return (
                <li key={s.id} data-testid={`today-seg-${s.id}`} className={`py-2 pl-3 pr-1 border-l-4 ${SEG_EDGE[s.kind] || SEG_EDGE.task} flex items-center justify-between text-sm`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate"><span className="mr-2"><KindTag kind={s.kind} /></span><b className={s.kind === "idle" ? "text-slate-500 font-medium" : "text-slate-800"}>{s.label}</b>
                      {merged ? <span data-testid={`asleep-tag-${s.id}`} className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">includes {fmtHM(sleeps.reduce((a, p) => a + (p.seconds || 0), 0))} asleep</span>
                        : s.asleep && <AsleepTag id={s.id} />}
                      {s.parts.some((p) => p.relabelled_from) && <span className="ml-2 text-[10px] text-slate-400">{merged ? "(reviewed)" : "(relabelled)"}</span>}
                      {s.outside_office && <span className="ml-2 text-[10px] text-slate-400">(only office hours counted)</span>}
                    </div>
                    <div className="text-xs text-slate-500 mono">{new Date(s.started_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} – {s.stopped_at && !s.running ? new Date(s.stopped_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : "running"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {toReview && (
                      <Button data-testid={`today-review-${s.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReviewSeg(toReview)}>Review</Button>
                    )}
                    <div className="mono text-sm tabular-nums text-slate-700">{fmtHM(s.seconds)}</div>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
          <RelabelDialog seg={reviewSeg} onClose={() => setReviewSeg(null)} onDone={() => { setReviewSeg(null); loadToday(); }} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 flex-wrap">
          {[["today", "Today"], ["7d", "Last 7d"], ["30d", "Last 30d"], ["month", "This month"], ["year", "This year"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`preset-${k}`} onClick={() => setPresetVal(k)} className={`px-3 py-1.5 text-xs rounded-sm ${preset === k ? "bg-white shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">From</Label>
          <Input data-testid="ts-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="mt-1 mono" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
          <Input data-testid="ts-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="mt-1 mono" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6 flex items-center gap-4">
        <Timer className="w-8 h-8 text-slate-400" />
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{scope === "team" ? "Team total" : "My total"} in range</div>
          <div className="text-3xl font-semibold mono" data-testid="ts-total">{data ? fmtDuration(data.total_seconds) : "—"}</div>
          <div className="text-xs text-slate-500">
            {data?.entry_count ?? 0} timer entries across {data?.rows?.length ?? 0} tasks
            {scope === "team" && data?.user_rows?.length ? ` · ${data.user_rows.length} teammates` : ""}
          </div>
        </div>
      </div>

      {/* Per-user breakdown (team scope) */}
      {scope === "team" && data?.user_rows?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6" data-testid="ts-user-rollup">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> By teammate <span className="text-xs text-slate-400 font-normal">· click a row to view / edit the day</span>
          </div>
          <div className="grid grid-cols-12 gap-2 px-5 py-2 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-1 text-right">Tasks</div>
            <div className="col-span-1 text-right">Entries</div>
            <div className="col-span-2 text-right">Time</div>
            <div className="col-span-2 text-right">Detail</div>
          </div>
          <ul className="divide-y divide-slate-200">
            {data.user_rows.map((u) => (
              <li
                key={u.user_id}
                data-testid={`ts-user-${u.user_id}`}
                className="grid grid-cols-12 gap-2 px-5 py-3 items-center hover:bg-slate-50 cursor-pointer"
                onClick={() => { setDrillDate(to || todayIso()); setDrillUser(u); }}
              >
                <div className="col-span-4 font-medium truncate">{u.user_name}</div>
                <div className="col-span-2 text-xs text-slate-500 capitalize">{u.role.replace("_", " ")}</div>
                <div className="col-span-1 text-right mono text-xs">{u.tasks}</div>
                <div className="col-span-1 text-right mono text-xs">{u.entries}</div>
                <div className="col-span-2 text-right mono text-sm font-medium">{fmtDuration(u.total_seconds)}</div>
                <div className="col-span-2 text-right">
                  <Button
                    data-testid={`ts-user-view-${u.user_id}`}
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); setDrillDate(to || todayIso()); setDrillUser(u); }}
                  >View day</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-semibold">By task</span>
          {data?.kind_totals && <TypeChips totals={data.kind_totals} testid="ts-type-totals" />}
        </div>
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <div className="col-span-4">Task</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Client · Entity</div>
          <div className="col-span-1 text-right">Entries</div>
          <div className="col-span-2 text-right">Time</div>
        </div>
        {loading ? <div className="p-6 text-center text-sm text-slate-500">Loading…</div> :
          !data?.rows?.length ? <div className="p-8 text-center text-sm text-slate-500">No time entries in this range.</div> : (
          <ul className="divide-y divide-slate-200">
            {data.rows.map((r) => (
              <li key={r.task_id} data-testid={`ts-row-${r.task_id}`} className={`grid grid-cols-12 gap-2 px-5 py-3 items-center border-l-4 ${SEG_EDGE[r.kind] || SEG_EDGE.task}`}>
                <div className="col-span-4 font-medium truncate">{r.task_name}</div>
                <div className="col-span-2"><KindTag kind={r.kind || "task"} /></div>
                <div className="col-span-3 text-xs truncate">{r.client_name || "—"} <span className="text-slate-400">·</span> {r.entity || "—"}</div>
                <div className="col-span-1 text-right mono text-xs">{r.entries}</div>
                <div className="col-span-2 text-right mono text-sm font-medium">{fmtDuration(r.total_seconds)}</div>
              </li>
            ))}
            <li data-testid="ts-total-row" className="grid grid-cols-12 gap-2 px-5 py-3 items-center bg-slate-50 font-semibold">
              <div className="col-span-9">Total</div>
              <div className="col-span-1 text-right mono text-xs">{data.entry_count}</div>
              <div className="col-span-2 text-right mono text-sm">{fmtDuration(data.total_seconds)}</div>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Per-user detailed view (Admin drill-down)
// Admins get add/edit/delete buttons for any segment on any date.
// -----------------------------------------------------------------------
function UserDetailedView({ userId, userName, initialDate, isAdmin, onBack }) {
  const [day, setDay] = useState(initialDate || todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSeg, setEditSeg] = useState(null); // seg or "new"
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tzOffset = -new Date().getTimezoneOffset();
      const { data } = await api.get(`/admin/timesheet/user/${userId}/daily?day=${day}&tz_offset_min=${tzOffset}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [userId, day]);
  useEffect(() => { load(); }, [load]);

  const deleteSeg = async (seg) => {
    if (!window.confirm(`Delete this ${seg.kind} segment?`)) return;
    try {
      await api.delete(`/admin/timesheet/segment/${seg.id}`);
      toast.success("Segment deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1"><ArrowLeft className="w-4 h-4" /> Back to timesheet</Button>
        {isAdmin && (
          <Button data-testid="admin-add-segment" size="sm" className="bg-blue-700 hover:bg-blue-800 text-white gap-1"
                  onClick={() => setEditSeg("new")}>
            <Plus className="w-3.5 h-3.5" /> Add entry
          </Button>
        )}
      </div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Detailed view</div>
        <h1 className="text-3xl font-semibold tracking-tight">{userName}</h1>
        <div className="flex items-center gap-3 mt-3">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Day</Label>
          <Input data-testid="admin-day-input" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mono w-fit" />
          {data && <div className="text-xs text-slate-500">Idle: <b className="mono text-slate-800">{fmtHM(data.idle_seconds)}</b></div>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <div className="col-span-2">Kind</div>
          <div className="col-span-4">Label / Task</div>
          <div className="col-span-3">Start – End</div>
          <div className="col-span-1 text-right">Duration</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 text-center text-sm text-slate-500">Loading…</div> :
         !data?.segments?.length ? <div className="p-8 text-center text-sm text-slate-500">No entries for this day.</div> : (
          <ul className="divide-y divide-slate-200">
            {data.segments.filter((s) => !isBlip(s)).map((s) => (
              <li key={s.id} data-testid={`admin-seg-${s.id}`} className={`grid grid-cols-12 gap-2 px-5 py-3 items-center text-sm border-l-4 ${SEG_EDGE[s.kind] || SEG_EDGE.task}`}>
                <div className="col-span-2"><KindTag kind={s.kind} /></div>
                <div className="col-span-4 truncate font-medium">{s.label}{s.running && <span className="text-[10px] ml-2 text-emerald-700">running</span>}{s.asleep && <AsleepTag id={s.id} />}{s.relabelled_from && <span className="ml-2 text-[10px] text-slate-400">(relabelled from {s.relabelled_from})</span>}</div>
                <div className="col-span-3 mono text-xs">
                  {new Date(s.started_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                  {" – "}
                  {s.stopped_at ? new Date(s.stopped_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : "…"}
                </div>
                <div className="col-span-1 text-right mono text-xs">{fmtHM(s.seconds)}</div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  {isAdmin && (
                    <>
                      <Button data-testid={`admin-edit-seg-${s.id}`} size="icon" variant="ghost" onClick={() => setEditSeg(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button data-testid={`admin-del-seg-${s.id}`} size="icon" variant="ghost" onClick={() => deleteSeg(s)}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editSeg && (
        <SegmentDialog
          seg={editSeg === "new" ? null : editSeg}
          userId={userId}
          day={day}
          onClose={() => setEditSeg(null)}
          onSaved={() => { setEditSeg(null); load(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// SegmentDialog — Admin add/edit form for a single time segment
// -----------------------------------------------------------------------
function SegmentDialog({ seg, userId, day, onClose, onSaved }) {
  const isEdit = !!seg;
  const [kind, setKind] = useState(seg?.kind || "break");
  const [reason, setReason] = useState(seg?.reason || "");
  const [startAt, setStartAt] = useState(seg?.started_at ? toLocalDT(seg.started_at) : `${day}T09:00`);
  const [stopAt, setStopAt] = useState(seg?.stopped_at ? toLocalDT(seg.stopped_at) : `${day}T09:30`);
  const [taskId, setTaskId] = useState(seg?.task_id || "");
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (kind !== "task") return;
    (async () => {
      try {
        const { data } = await api.get(`/tasks?scope=team&view=active`);
        setTasks(data.slice(0, 500));
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [kind]);

  const submit = async () => {
    if (!startAt || !stopAt) { toast.error("Start and end are required"); return; }
    if (new Date(stopAt) <= new Date(startAt)) { toast.error("End must be after start"); return; }
    setBusy(true);
    try {
      const payload = {
        user_id: userId, kind,
        reason: reason.trim() || null,
        started_at: fromLocalDT(startAt), stopped_at: fromLocalDT(stopAt),
        task_id: kind === "task" ? (taskId || null) : null,
      };
      if (isEdit) {
        await api.patch(`/admin/timesheet/segment/${seg.id}`, payload);
        toast.success("Segment updated");
      } else {
        await api.post("/admin/timesheet/segment", payload);
        toast.success("Segment added");
      }
      onSaved?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!busy && !o) onClose(); }}>
      <DialogContent data-testid="admin-segment-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Edit segment" : "Add segment"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="admin-seg-kind" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="idle">Idle / Unassigned</SelectItem>
                <SelectItem value="break">Break</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="task">Task</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kind === "task" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Task</Label>
              <Select value={taskId} onValueChange={setTaskId}>
                <SelectTrigger data-testid="admin-seg-task" className="mt-1"><SelectValue placeholder="Select task…" /></SelectTrigger>
                <SelectContent>
                  {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {["break", "meeting", "call"].includes(kind) && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reason</Label>
              <Input data-testid="admin-seg-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Lunch, Client meeting…" className="mt-1" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Start</Label>
              <Input data-testid="admin-seg-start" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="mt-1 mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">End</Label>
              <Input data-testid="admin-seg-end" type="datetime-local" value={stopAt} onChange={(e) => setStopAt(e.target.value)} className="mt-1 mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="admin-seg-save" onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add segment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
